import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { acpPromotions, acpPromotionToggle, acpSettingsGet, acpSettingsSave } from "@/lib/api";
import { Section, StatusPill } from "./acpUi";
import { AcpSftpSettings } from "./AcpSftpSettings";

export function AcpPromotions({ caller }) {
  const [rows, setRows] = useState(null);
  const load = () => acpPromotions(caller).then((r) => setRows(r.promotions || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [caller]); // eslint-disable-line react-hooks/exhaustive-deps
  async function toggle(c, on) {
    try {
      await acpPromotionToggle(caller, c.id, on);
      setRows((rs) => rs.map((x) => (x.id === c.id ? { ...x, acp_eligible: on } : x)));
      toast.success(`${c.code} ${on ? "enabled" : "disabled"} for ChatGPT`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  }
  if (!rows) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>;
  return (
    <Section title="Promotions" right={<span className="text-xs text-zinc-500">{rows.filter((r) => r.acp_eligible).length} active for ChatGPT</span>}>
      <p className="text-xs text-zinc-500 mb-3">Your existing coupons. Flip a code on to let ChatGPT shoppers apply it at Instant Checkout — the same code keeps working on the web shop.</p>
      <ul className="divide-y divide-zinc-100" data-testid="acp-promotions">
        {rows.map((c) => (
          <li key={c.id} className="flex items-center justify-between py-2.5" data-testid={`acp-promo-${c.id}`}>
            <div className="min-w-0">
              <div className="font-mono text-sm font-semibold text-zinc-900">{c.code}</div>
              <div className="text-xs text-zinc-500">{c.summary}{c.expires ? ` · expires ${c.expires}` : ""}{c.active ? "" : " · expired"}</div>
            </div>
            <Switch checked={c.acp_eligible} onCheckedChange={(v) => toggle(c, v)} data-testid={`acp-promo-toggle-${c.id}`} />
          </li>
        ))}
        {rows.length === 0 && <li className="text-xs text-zinc-500 py-3">No coupons found.</li>}
      </ul>
    </Section>
  );
}

const MODES = [["pending", "Pending Approval"], ["test", "Test Mode"], ["active", "Active"]];

export function AcpSettings({ caller }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { acpSettingsGet(caller).then(setS).catch(() => setS({ error: true })); }, [caller]);
  async function save(patch) {
    setSaving(true);
    try {
      setS(await acpSettingsSave(caller, patch));
      toast.success("ChatGPT Commerce settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }
  if (!s) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>;
  if (s.error) return <div className="text-sm text-zinc-500">Couldn't load settings.</div>;
  const ig = s.integrations;
  return (
    <div className="space-y-4" data-testid="acp-settings">
      <Section title="Configuration">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-600">Seller name (shown in ChatGPT)</Label>
            <Input defaultValue={s.seller_name} onBlur={(e) => e.target.value !== s.seller_name && save({ seller_name: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="acp-seller-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-600">Terms of service URL</Label>
            <Input defaultValue={s.terms_url} onBlur={(e) => e.target.value !== s.terms_url && save({ terms_url: e.target.value })} className="h-10 rounded-xl mt-1" data-testid="acp-terms-url" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 py-2 border-t border-zinc-100">
          <div><div className="text-sm font-medium text-zinc-900">Product feed enabled</div><div className="text-xs text-zinc-500">Serve the product snapshot for ChatGPT product discovery</div></div>
          <Switch checked={s.feed_enabled} onCheckedChange={(v) => save({ feed_enabled: v })} data-testid="acp-feed-enabled" />
        </div>
        <div className="py-2 border-t border-zinc-100">
          <div className="text-sm font-medium text-zinc-900 mb-1">Instant Checkout mode</div>
          <div className="inline-flex rounded-xl bg-zinc-100 p-0.5" data-testid="acp-checkout-mode">
            {MODES.map(([v, l]) => (
              <button key={v} type="button" disabled={saving} onClick={() => save({ checkout_mode: v })} className={`h-8 px-3 rounded-lg text-xs font-semibold ${s.checkout_mode === v ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`} data-testid={`acp-mode-${v}`}>{l}</button>
            ))}
          </div>
          <div className="text-xs text-zinc-500 mt-1">Test Mode accepts sandbox tokens without charging. Active requires the OpenAI request secret on the server.</div>
        </div>
      </Section>
      <AcpSftpSettings key={JSON.stringify(s.sftp)} caller={caller} sftp={s.sftp} onSaved={setS} />
      <Section title="Integration status (secrets stay on the server)">
        <ul className="divide-y divide-zinc-100 text-sm">
          <li className="flex items-center justify-between py-2"><span>OpenAI merchant SFTP (feed delivery)</span><StatusPill status={s.sftp.configured ? "connected" : "not_connected"} testId="acp-int-sftp" /></li>
          <li className="flex items-center justify-between py-2"><span>OpenAI Commerce feed API (merchant key + base URL)</span><StatusPill status={ig.openai.commerce_api_key && ig.openai.commerce_base_url ? "connected" : "not_connected"} testId="acp-int-openai-commerce" /></li>
          <li className="flex items-center justify-between py-2"><span>OpenAI / ACP request secret</span><StatusPill status={ig.openai.request_secret ? "connected" : "not_connected"} testId="acp-int-openai" /></li>
          <li className="flex items-center justify-between py-2"><span>OpenAI order webhook</span><StatusPill status={ig.openai.webhook_url && ig.openai.webhook_secret ? "connected" : "not_connected"} /></li>
          <li className="flex items-center justify-between py-2"><span>Stripe</span><StatusPill status={ig.stripe.mode === "live" ? "connected" : ig.stripe.mode === "test" ? "test_mode" : "not_connected"} testId="acp-int-stripe" /></li>
          <li className="flex items-center justify-between py-2"><span>ShipStation</span><StatusPill status={ig.shipstation.configured ? "connected" : "not_connected"} testId="acp-int-shipstation" /></li>
        </ul>
        <div className="mt-3 text-xs text-zinc-500">Keys are managed under <b>Manage → Settings → Integrations</b> (server environment). Endpoints to give OpenAI:</div>
        <code className="block text-[11px] bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 mt-1 break-all" data-testid="acp-endpoint-checkout">{s.endpoints.checkout_sessions}</code>
        <code className="block text-[11px] bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 mt-1 break-all">{s.endpoints.feed}</code>
        <code className="block text-[11px] bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 mt-1 break-all" data-testid="acp-endpoint-sitemap">{s.endpoints.sitemap}</code>
      </Section>
      <div className="text-right"><Button variant="outline" size="sm" disabled className="rounded-lg">{saving ? "Saving…" : "Changes save automatically"}</Button></div>
    </div>
  );
}
